import { useReducer, useCallback, useEffect, useMemo } from 'react'

export type FilterMap = { [k: string]: number | string | (number|string)[] | null }
type FilterListAction = {
    type: string,
    payload?: any
}

const initialState: FilterMap = {}
const reducer = (state: FilterMap, action: FilterListAction): FilterMap => {
    switch (action.type) {
        case 'updateFilter': {
            return { ...state, [action.payload.field]: action.payload.value }
        }
        case 'updateFilters': {
            const nextState = { ...state }
            Object.keys(action.payload).forEach((k: string) => {
                if (action.payload[k] !== undefined) {
                    nextState[k] = action.payload[k]
                } else {
                    delete nextState[k]
                }
            })
            return nextState
        }
        case 'removeFilter': {
            const { [action.payload.field]: removedFilter, ...newState } = state
            return newState
        }
        case 'resetFilters': {
            return action.payload ? action.payload : {}
        }
        /* v8 ignore next 2 */ // should never get to here but it's good to have defaults
        default:
            throw new Error('unknown action type')
    }
}

const actions = {
    updateFilter: (field: string, value: number | string | (number|string)[] | null) => ({
        type: 'updateFilter',
        payload: {
            field,
            value
        }
    }),
    updateFilters: (filters: FilterMap) => ({
        type: 'updateFilters',
        payload: filters
    }),
    removeFilter: (field: string) => ({
        type: 'removeFilter',
        payload: { field }
    }),
    resetFilters: (filters?: any) => ({
        type: 'resetFilters',
        payload: filters
    })
}


export const useFiltersList = (valueExpr?: string, value?: any, extraFilters: FilterMap | null = null) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const setFilter = useCallback((fieldName: string, value: string | number | (string|number)[] | null) => {
        dispatch(actions.updateFilter(fieldName, value))
    }, [])

    const removeFilter = useCallback((fieldName: string) => {
        dispatch(actions.removeFilter(fieldName))
    }, [])

    const resetFilters = useCallback((filters?: any) => {
        dispatch(actions.resetFilters({ ...( extraFilters || {} ), ...filters }))
    }, [extraFilters])

    useEffect(() => {
        // we should not set any filters if any of the two arguments is missing
        if (!(value !== undefined && valueExpr !== undefined)) { return }

        if (value === null || (Array.isArray(value) && value.length === 0)) {
            removeFilter(valueExpr.toLowerCase())
        } else {
            if (Array.isArray(value)) {
                setFilter(valueExpr.toLowerCase(), value.map((v: any) => v[valueExpr]))
            } else {
                setFilter(valueExpr.toLowerCase(), value[valueExpr])
            }
        }
    }, [valueExpr, value, setFilter, removeFilter])

    useEffect(() => {
        if (extraFilters) {
            dispatch(actions.updateFilters(extraFilters))
        }
    }, [extraFilters])

    const ret = useMemo(() => ({
        state, setFilter, removeFilter, resetFilters
    }), [state, setFilter, removeFilter, resetFilters])

    return ret
}
